import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { fetchDialogueTopics, fetchDiscussionTopics } from '../utils/contentApi';
import { isReactSnap } from '../bootstrap/bootRuntime';
import { A2_SECTION_TOPIC_IDS, B1_FREE_SAMPLE_TOPIC_IDS } from '../pages/Practice/topicCatalog';
import { isGatedMaterial } from '../pages/Practice/practiceMaterials';

export const useAccessTier = () => {
  const { user, isPremium, loading, profileLoading } = useAuth();
  return loading || (user && profileLoading) ? null : isPremium ? 'premium' : 'preview';
};

const NO_LOCKED_IDS = new Set();

export const useLockedTopicIds = (track) => {
  const { user } = useAuth();
  const accessTier = useAccessTier();
  const fetchTopics = track === 'discussion' ? fetchDiscussionTopics : fetchDialogueTopics;
  const enabled = Boolean(track) && accessTier !== null && !isReactSnap();
  const query = useQuery({
    queryKey: ['topics', track, accessTier, user?.uid ?? 'guest'],
    queryFn: async () => fetchTopics(user ? await user.getIdToken() : null),
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const rawLockedIds = useMemo(() => {
    if (accessTier === 'premium' || !Array.isArray(query.data)) return new Set();
    return new Set(query.data.filter((topic) => topic?.hidden === true).map((topic) => Number(topic.id)));
  }, [accessTier, query.data]);

  /**
   * A guest can't be told, from the API, which discussion topics are hidden
   * because they're paid versus hidden only because there's no token — the
   * backend returns every one of them as hidden to an unauthenticated request
   * on purpose (see french-fide-be's content route: a guest gets "the full
   * locked catalog shape ... so calling the API directly cannot bypass the
   * UI"). But B1_FREE_SAMPLE_TOPIC_IDS is stable, authored content (pinned to
   * a backend test), so a guest topic can still be split correctly by id:
   * one of the three samples asks for a free account; anything else really is
   * paid, same as it is once signed in.
   */
  const isDiscussionGuest = track === 'discussion' && !user;
  const { lockedIds, loginRequiredIds } = useMemo(() => {
    if (!isDiscussionGuest) return { lockedIds: rawLockedIds, loginRequiredIds: NO_LOCKED_IDS };

    return {
      lockedIds: new Set([...rawLockedIds].filter((id) => !B1_FREE_SAMPLE_TOPIC_IDS.has(id))),
      loginRequiredIds: new Set([...rawLockedIds].filter((id) => B1_FREE_SAMPLE_TOPIC_IDS.has(id))),
    };
  }, [isDiscussionGuest, rawLockedIds]);

  return { ...query, lockedIds, loginRequiredIds, accessTier };
};

/**
 * The topics a material locks, whichever material it is.
 *
 * Dialogue and discussion ask the API which topics are hidden; vocabulary and
 * flashcards don't need to, because the paid word sets are the three A2 exam-task
 * ones and the client already knows which those are. The backend still enforces it
 * — `/content/default-vocab` returns them empty — so this only draws the padlock.
 *
 * `loginRequiredIds` only ever has entries for discussion topics a guest hasn't
 * signed in for — vocab/flashcards' gate stays premium-only, no login tier.
 */
export const useLockedMaterialTopicIds = (material) => {
  const { lockedIds, loginRequiredIds } = useLockedTopicIds(isGatedMaterial(material) ? material : null);
  const accessTier = useAccessTier();

  return useMemo(() => {
    if (material !== 'vocab' && material !== 'flashcards') return { lockedIds, loginRequiredIds };
    const vocabLockedIds = accessTier === null || accessTier === 'premium' ? NO_LOCKED_IDS : A2_SECTION_TOPIC_IDS;
    return { lockedIds: vocabLockedIds, loginRequiredIds: NO_LOCKED_IDS };
  }, [accessTier, lockedIds, loginRequiredIds, material]);
};
